/**
 * Full-page technician create.
 * Partner (ADMIN) chosen from dropdown; FIO + phone; password confirmation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import { getCurrentEmail, getCurrentRole } from '../utils/accessControl';

function normalizeAdmins(raw) {
  const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  return list
    .map((item) => {
      const email = (typeof item === 'string' ? item : item?.email || item?.login || '').trim();
      if (!email) return null;
      return { email, label: email };
    })
    .filter(Boolean)
    .sort((a, b) => a.email.localeCompare(b.email));
}

export default function ServiceTechnicianCreatePage() {
  const { t } = useTranslation();
  const history = useHistory();
  const role = getCurrentRole();
  const selfEmail = getCurrentEmail();
  const isSa = role === 'SUPER_ADMIN';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [partnerEmail, setPartnerEmail] = useState(isSa ? '' : selfEmail);
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isSa) return undefined;
    let cancelled = false;
    (async () => {
      setPartnersLoading(true);
      try {
        const { data } = await api.getAdmins();
        if (!cancelled) setPartners(normalizeAdmins(data));
      } catch {
        if (!cancelled) setPartners([]);
      } finally {
        if (!cancelled) setPartnersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSa]);

  const passwordMismatch = useMemo(
    () => Boolean(passwordConfirm) && password !== passwordConfirm,
    [password, passwordConfirm],
  );

  const validate = () => {
    const next = {};
    if (!email.trim()) next.email = t('service.validationRequired');
    if (!fullName.trim()) next.fullName = t('service.validationRequired');
    if (!phone.trim()) next.phone = t('service.validationRequired');
    if (!password) next.password = t('service.validationRequired');
    else if (password.length < 6) next.password = t('service.validationPasswordShort');
    if (!passwordConfirm) next.passwordConfirm = t('service.validationRequired');
    else if (password !== passwordConfirm) next.passwordConfirm = t('service.validationPasswordMismatch');
    if (isSa && !partnerEmail) next.partnerEmail = t('service.validationRequired');
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      const body = {
        email: email.trim(),
        password,
        name: fullName.trim(),
        phone: phone.trim(),
      };
      if (isSa) {
        body.parentAdminEmail = partnerEmail;
      }
      await api.createServiceTechnician(body);
      history.replace('/service/technicians');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('service.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer title={t('service.createTech')} breadcrumbs={[]}>
      <Stack component="form" onSubmit={onSubmit} spacing={2.5} sx={{ maxWidth: 640 }}>
        {error && <Alert severity="error">{error}</Alert>}

        {isSa ? (
          <TextField
            select
            label={t('service.partner')}
            value={partnerEmail}
            onChange={(e) => setPartnerEmail(e.target.value)}
            required
            fullWidth
            disabled={partnersLoading}
            error={Boolean(fieldErrors.partnerEmail)}
            helperText={fieldErrors.partnerEmail || t('service.partnerHint')}
          >
            <MenuItem value="">
              <em>{partnersLoading ? '…' : t('service.partnerSelect')}</em>
            </MenuItem>
            {partners.map((p) => (
              <MenuItem key={p.email} value={p.email}>{p.label}</MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            label={t('service.partner')}
            value={partnerEmail}
            fullWidth
            InputProps={{ readOnly: true }}
            helperText={t('service.partnerSelfHint')}
          />
        )}

        <TextField
          label={t('service.techFullName')}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          fullWidth
          error={Boolean(fieldErrors.fullName)}
          helperText={fieldErrors.fullName || t('service.techFullNameHint')}
        />

        <TextField
          label={t('service.techPhone')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
          fullWidth
          error={Boolean(fieldErrors.phone)}
          helperText={fieldErrors.phone}
        />

        <TextField
          label={t('service.techEmail')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          error={Boolean(fieldErrors.email)}
          helperText={fieldErrors.email}
        />

        <TextField
          label={t('service.techPassword')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
          autoComplete="new-password"
          error={Boolean(fieldErrors.password)}
          helperText={fieldErrors.password}
        />
        <TextField
          label={t('service.techPasswordConfirm')}
          type="password"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          required
          fullWidth
          autoComplete="new-password"
          error={Boolean(fieldErrors.passwordConfirm) || passwordMismatch}
          helperText={fieldErrors.passwordConfirm || (passwordMismatch ? t('service.validationPasswordMismatch') : ' ')}
        />

        <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={saving}
            sx={{
              minWidth: 180,
              color: (theme) => theme.palette.primary.contrastText,
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            {t('service.saveTech')}
          </Button>
          <Button
            size="large"
            variant="outlined"
            onClick={() => history.push('/service/technicians')}
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
        </Stack>
      </Stack>
    </PageContainer>
  );
}
